import { RefreshCcw, RotateCcw } from "lucide-react";
import { useMemo, useState } from "react";

import { Load } from "components/load";
import PageError from "components/page-error";
import { Button } from "components/ui/button";

import { useGetDepositStoresSummaryQuery } from "@/services/deposit-reconciliation";
import type { IDepositStoresSummaryParams } from "src/@types/deposit-reconciliation";
import { useMeQuery } from "@/services/user";
import { isUserAdminInGroup } from "@/utils/user";
import { layoutClasses } from "@/utils/css-classes";
import { useDepositStoresFilterActions } from "./ducks/filter/hooks/actions";
import { useDepositStoresFilterState } from "./ducks/filter/hooks/filter-state";
import { columns } from "./columns";
import DepositStoresTable from "./components/table";
import SelectFilters from "./components/select-filters";
import { parseDivergenceRange } from "./constants/divergence-ranges";
import ReprocessModal from "../components/reprocess-modal";
import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { isValidPeriod } from "@/utils/date-range";

const PAGE_SIZE = 20;

export default function DepositStoresPanel() {
  const { updateFilter } = useDepositStoresFilterActions();
  const filterState = useDepositStoresFilterState();
  const { filters } = filterState || { filters: {} };

  const periodStart = filters.periodStart;
  const periodEnd = filters.periodEnd;

  const divergence = parseDivergenceRange(filters.divergenceRange);

  const storeCodes =
    Array.isArray(filters.storeCodes) && filters.storeCodes.length > 0
      ? filters.storeCodes.map(Number).filter(Number.isFinite)
      : undefined;

  const status = filters.status as IDepositStoresSummaryParams["status"];

  const pageSize: number = filters.pageSize ?? PAGE_SIZE;

  const queryParams: IDepositStoresSummaryParams = {
    periodStart,
    periodEnd,
    storeCodes,
    status,
    regionalId: filters.regionalId ? Number(filters.regionalId) : undefined,
    responsibleUserId: filters.responsibleUserId || undefined,
    ...divergence,
    page: filters.meta?.page || 1,
    pageSize,
  };

  const periodValid: boolean = isValidPeriod(periodStart, periodEnd);

  const { data, isLoading, isFetching, error, refetch } =
    useGetDepositStoresSummaryQuery(queryParams, {
      refetchOnFocus: false,
      skip: !periodValid,
    });

  const [reprocessOpen, setReprocessOpen] = useState(false);
  const { data: currentUser } = useMeQuery();
  const isAdmin = useMemo(() => {
    const moduleAccess = localStorage.getItem("@verzel:module");
    return isUserAdminInGroup(currentUser, moduleAccess ?? "");
  }, [currentUser]);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const currentPage = data?.page ?? queryParams.page ?? 1;

  const handlePeriodChange = (
    start: string | undefined,
    end: string | undefined,
  ) => {
    updateFilter({ key: "periodStart", value: start });
    updateFilter({ key: "periodEnd", value: end });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  if (error) return <PageError status={500} message="ERRO INTERNO NO SERVIDOR" />;
  if ((isLoading || isFetching) && rows.length === 0) return <Load />;

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-end gap-2 mb-6">
        <PeriodRangeFilter
          start={periodStart}
          end={periodEnd}
          onChange={handlePeriodChange}
        />
      </div>

      <div className={layoutClasses.contentWrapper}>
        <div className="p-0">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 flex-wrap">
                <SelectFilters />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReprocessOpen(true)}
                  className="flex items-center gap-2 border-[#2B0C1A] text-[#2B0C1A] hover:bg-[#2B0C1A] hover:text-white"
                >
                  <RefreshCcw className="w-4 h-4" />
                  Reprocessar
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-2 min-w-[120px] border-[#2B0C1A] text-[#2B0C1A] hover:bg-[#2B0C1A] hover:text-white"
              >
                <RotateCcw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
                {isFetching ? "Carregando..." : "Recarregar"}
              </Button>
            </div>
          </div>
        </div>

        <div className={layoutClasses.tableContainer}>
          <div className={layoutClasses.tableWrapper}>
            <DepositStoresTable
              data={rows}
              columns={columns}
              page={currentPage}
              pageSize={pageSize}
              total={total}
              isFetching={isFetching}
            />
          </div>
        </div>
      </div>

      <ReprocessModal
        open={reprocessOpen}
        onClose={() => setReprocessOpen(false)}
        defaultPeriodStart={periodStart}
        defaultPeriodEnd={periodEnd}
      />
    </div>
  );
}
